import { ReactElement } from 'react'
import { styled, ButtonBase, TypographyProps, Typography } from '@mui/material'
import { WappIcon } from '$comp/chat-list-tool-box/components/shared/wapp-icon'

export type ChatListToolboxListItemVariant = 'fullWidth' | 'halfWidth'

interface Props {
  icon: ReactElement
  title: string
  description: string
  borderColor: string
  backgroundColor: string
  endIconButton?: ReactElement
  onClick: () => void
  variant: ChatListToolboxListItemVariant
  gradient: string
  hoverColor: string
  className?: string
}

export function ChatListToolboxListItem({
  icon,
  title,
  description,
  borderColor,
  backgroundColor,
  endIconButton,
  onClick,
  variant,
  gradient,
  className,
  hoverColor,
}: Props) {
  return (
    <Root
      className={className}
      sx={{
        borderColor,
        backgroundColor,
        gridColumn: variant === 'fullWidth' ? '1 / 3' : undefined,
        '&:hover': {
          backgroundColor: hoverColor,
        },
      }}
      onClick={onClick}
    >
      <ContentWrapper className={variant}>
        {variant === 'fullWidth' && (
          <>
            <WappIcon background={gradient} icon={icon} />
            <TitleDescriptionWrapper>
              <Title>{title}</Title>
              <Description>{description}</Description>
            </TitleDescriptionWrapper>
          </>
        )}

        {variant === 'halfWidth' && (
          <>
            <IconTitleWrapper>
              <WappIcon background={gradient} icon={icon} />
              <Title>{title}</Title>
            </IconTitleWrapper>
            <Description>{description}</Description>
          </>
        )}
      </ContentWrapper>
      {endIconButton}
    </Root>
  )
}

const Root = styled(ButtonBase)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid;
  transition: background-color 0.2s ease;
`

const ContentWrapper = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  &.halfWidth {
    flex-direction: column;
  }
`

const IconTitleWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const TitleDescriptionWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const Title = (props: TypographyProps) => (
  <Typography {...props} variant="subtitle3" color="neutral1000" />
)

const Description = (props: TypographyProps) => (
  <Typography
    {...props}
    variant="paragraph3"
    color="neutral800"
    textAlign="right"
  />
)
